
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField } from "./BaseForm";
import { CyberInsuranceFormData, FormProps } from "@/types/forms";

type CyberInsuranceFormProps = FormProps<CyberInsuranceFormData>;

const CyberInsuranceForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: CyberInsuranceFormProps) => {
  const [formData, setFormData] = useState<CyberInsuranceFormData>({
    businessType: initialData.businessType || "",
    storeCustomerData: initialData.storeCustomerData || "",
    annualTurnover: initialData.annualTurnover || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.businessType) newErrors.businessType = "Business type is required";
    if (!formData.storeCustomerData) newErrors.storeCustomerData = "Please specify if you store customer data";
    if (!formData.annualTurnover) newErrors.annualTurnover = "Annual turnover is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof CyberInsuranceFormData>(field: K, value: CyberInsuranceFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Type of business (e.g. e-commerce, finance, etc.)" required>
        <Input
          placeholder="Enter your business type"
          value={formData.businessType}
          onChange={(e) => updateField('businessType', e.target.value)}
        />
        {errors.businessType && <p className="text-red-500 text-sm">{errors.businessType}</p>}
      </FormField>

      <FormField label="Do you store customer data?" required>
        <Select value={formData.storeCustomerData} onValueChange={(value) => updateField('storeCustomerData', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        {errors.storeCustomerData && <p className="text-red-500 text-sm">{errors.storeCustomerData}</p>}
      </FormField>

      <FormField label="Annual turnover (AED)" required>
        <Input
          type="number"
          placeholder="Enter annual turnover in AED"
          value={formData.annualTurnover}
          onChange={(e) => updateField('annualTurnover', e.target.value)}
        />
        {errors.annualTurnover && <p className="text-red-500 text-sm">{errors.annualTurnover}</p>}
      </FormField>

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default CyberInsuranceForm;
